import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  HeartPulse,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  TrendingUp,
  UserRound,
  Layers,
  Palette,
  AlertTriangle,
  ScanLine,
  Scissors,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/mole-removal-mumbai";
const CYST_PATH = "/sebaceous-cyst-removal-mumbai";
const LIPOMA_PATH = "/lipoma-removal-surgery-mumbai";
const SCAR_PATH = "/hypertrophic-scar-treatment-in-mumbai";

const MOLE_FAQS = [
  {
    question: "Does mole removal cause cancer?",
    answer:
      "No. Properly performed mole removal does not cause cancer. Complete surgical excision removes the mole entirely and sends it for histological analysis, which confirms the diagnosis. The concern sometimes raised is that incomplete removal leaves cells that could become problematic. This is why complete excision with confirmed clear margins is the standard for any mole with suspicious features, and why partial treatments like laser are reserved for clinically confirmed benign moles only.",
  },
  {
    question: "Will a mole grow back after removal?",
    answer:
      "After complete surgical excision with confirmed clear margins, recurrence is uncommon. After radiofrequency or laser treatment, there is a small risk of partial regrowth if any mole cells remain beneath the treated surface. This is why RF and laser techniques are used only for clinically benign moles where recurrence, while inconvenient, does not carry the same concern as incomplete removal of a suspicious lesion. Any area that shows regrowth after removal should be assessed and re-excised surgically.",
  },
  {
    question: "Can I remove a mole at home?",
    answer:
      "No. Home removal attempts using acid creams, sharp instruments, or ligation damage the surrounding skin, frequently cause scarring that is significantly worse than professional removal, and do not remove the mole completely, resulting in recurrence. More importantly, home removal destroys the tissue, making histological analysis of the mole impossible. If a mole turns out to have suspicious features, this information is lost. Professional removal is the only safe approach.",
  },
  {
    question: "How do I know if my mole is dangerous?",
    answer:
      "The ABCDE rule provides a practical assessment framework: Asymmetry, Border irregularity, Color variation, Diameter greater than 6 millimeters, and Evolving in any way. Any one of these features is sufficient reason for prompt clinical assessment. The majority of moles assessed for these features are confirmed benign on examination. The purpose of assessment is to confirm this with certainty or to identify the minority that require treatment at the earliest possible stage.",
  },
  {
    question: "Is mole removal painful?",
    answer:
      "The procedure is performed under local anesthesia. The anesthesia injections produce brief discomfort at the injection sites. The removal itself is painless. Post-procedure mild redness and minor tenderness for 1 to 3 days are the most common experiences. Most patients describe the procedure as significantly less uncomfortable than they anticipated.",
  },
  {
    question: "Will I have a scar after mole removal?",
    answer:
      "Any procedure that removes tissue from the skin creates some change to the skin surface. The goal is to minimize this as much as possible through technique selection and precise closure. Surgical excision leaves a linear scar that is oriented along natural skin tension lines and fades over 6 to 12 months. RF removal leaves a circular healing area that typically fades to near-invisible on the face over 3 to 6 months. Laser treatment leaves a healing area similar to RF. The final appearance depends on the mole's location, the technique used, and the patient's skin healing characteristics.",
  },
  {
    question: "Can multiple moles be removed in one session?",
    answer:
      "Yes. Multiple moles can be assessed dermoscopically and appropriate ones removed in the same session when their locations and total number allow. Dr. Jain evaluates each mole individually during consultation and discusses which ones are suitable for removal, which require a different technique, and whether any need histological priority. Removing multiple moles in one session is practical and avoids multiple separate recovery periods.",
  },
  {
    question: "How long does the area take to heal after mole removal?",
    answer:
      "Initial healing takes 1 to 2 weeks. The area is closed and comfortable within this period. Scar maturation continues over 6 to 12 months as the collagen in the scar remodels and the pigmentation normalizes. Sun protection of the treated area during the maturation phase is the single most important factor in the final scar appearance. UV exposure to a healing scar causes permanent pigmentary darkening that significantly worsens the final result.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Melanocytic nevus",
      alternateName: ["Mole", "Nevus"],
      description:
        "Benign cluster of melanocytes presenting as pigmented skin lesions; may be removed for cosmetic reasons or assessed and excised when clinical features warrant medical evaluation.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Skin" },
      possibleTreatment: [
        "Surgical excision with histological analysis",
        "Radiofrequency shave excision",
        "Laser mole removal (selected flat benign lesions)",
      ],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Mole removal",
      description:
        "Cosmetic or medically indicated removal of melanocytic nevi using surgical excision, radiofrequency, or laser under local anesthesia with dermoscopic assessment and histology when indicated.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: MOLE_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function MoleJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Mole Removal", href: "/mole-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Skin &amp; Soft Tissue</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Mole Removal</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Cosmetic &amp; Medical Mole Removal by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Mole Removal" icon="●" size="hero"  imageSrc={HERO.moleRemoval} imageAlt="Mole removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is a Mole?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A mole, clinically called a nevus, is a cluster of melanocytes, the pigment-producing cells in the skin, that has grown as a group rather than distributing evenly. Moles appear as brown, black, or skin-colored spots or raised lesions on the skin surface. Most adults have between 10 and 40 moles. The majority are entirely benign, remain stable throughout life, and require no treatment.
            </p>
            <p>
              Moles are removed for two distinct reasons. The first is cosmetic: a mole that is visible on the face, neck, or another prominent area and is unwanted for aesthetic reasons. The second is medical: a mole that has changed in size, shape, color, or behavior in a way that raises concern about whether it remains benign. The reason for removal determines the technique used, whether a biopsy is required, and how the excised tissue is handled afterward.
            </p>
            <p>
              Dr. Vicky Jain performs mole removal using surgical excision, radiofrequency technique, or laser depending on the type of mole, its size and depth, and whether the removal is primarily cosmetic or medically indicated. Every excised specimen from a surgical removal is sent for histological analysis to confirm the diagnosis.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Mole Removal</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The first step in any mole removal consultation is clinical assessment of whether the mole is benign or requires medical evaluation. Dr. Jain examines each mole using dermoscopy, a technique that magnifies the mole&apos;s internal structure and reveals features not visible to the naked eye. Dermoscopy significantly improves the accuracy of clinical diagnosis for pigmented lesions and reduces the rate of unnecessary removal of benign moles and missed diagnoses of concerning ones.
              </p>
              <p>
                For moles with reassuring clinical and dermoscopic features being removed for cosmetic reasons, the technique is selected to minimize scarring while achieving complete removal. For moles with any concerning feature, surgical excision with an appropriate margin is performed regardless of the patient&apos;s cosmetic preference, and the specimen is sent urgently for histological analysis. The diagnosis takes precedence over the cosmetic outcome when there is any clinical concern.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhenConsiderSection() {
  const cards = [
    {
      title: "Cosmetic Concern",
      icon: Sparkles,
      copy: "A mole that is visible on the face, neck, or another prominent area and affects appearance or confidence. Cosmetic removal is appropriate for any stable, clinically benign mole that the patient wants removed for aesthetic reasons.",
    },
    {
      title: "Raised or Prominent Mole",
      icon: Layers,
      copy: "Raised moles that catch on clothing, jewelry, or are frequently irritated by shaving or grooming. Repeated minor trauma to a mole can cause bleeding and inflammation that is uncomfortable and creates ongoing concern.",
    },
    {
      title: "Change in Size",
      icon: TrendingUp,
      copy: "A mole that has grown noticeably over weeks or months requires prompt medical assessment. Gradual change in size is one of the most important warning signs that a mole needs evaluation rather than simple observation.",
    },
    {
      title: "Change in Shape or Border",
      icon: ScanLine,
      copy: "A mole that has developed an irregular, jagged, or poorly defined border where it previously had a smooth, well-defined edge requires clinical and dermoscopic assessment before any decision on removal.",
    },
    {
      title: "Color Change or Variation",
      icon: Palette,
      copy: "A mole developing multiple shades, areas of darker pigmentation, or loss of color in parts of the lesion requires assessment. Uniform color is a reassuring feature. Variation in color within a single mole is a warning sign.",
    },
    {
      title: "Bleeding, Itching, or Crusting",
      icon: HeartPulse,
      copy: "A mole that bleeds without significant trauma, itches persistently, or develops crusting on its surface requires prompt assessment. These features indicate a change in the behavior of the mole that needs clinical evaluation.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="when-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">When Should You Consider Mole Removal?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbcdeSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="abcde-section" aria-labelledby="abcde-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Warning Signs</p>
        <h2 id="abcde-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
          The ABCDE Rule: Warning Signs in a Mole
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-[#FAFAF8] max-w-4xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-2">When to seek medical assessment</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              The ABCDE rule is a clinical tool for identifying features in a mole that require medical assessment. These features do not confirm that a mole is malignant, but any one of them is sufficient reason to seek prompt evaluation by a qualified doctor rather than waiting.
            </p>
            <ol className="list-none space-y-4 text-deep-navy/80 leading-relaxed m-0 p-0">
              <li>
                <strong className="text-deep-navy">A: Asymmetry.</strong> One half of the mole does not match the other half in shape.
              </li>
              <li>
                <strong className="text-deep-navy">B: Border.</strong> The edges are irregular, ragged, notched, or poorly defined.
              </li>
              <li>
                <strong className="text-deep-navy">C: Color.</strong> The mole has more than one shade, with variations of brown, black, or areas of red, white, or blue.
              </li>
              <li>
                <strong className="text-deep-navy">D: Diameter.</strong> The mole is larger than 6 millimeters across, approximately the size of a pencil eraser, or has grown.
              </li>
              <li>
                <strong className="text-deep-navy">E: Evolving.</strong> The mole is changing in any way: size, shape, color, or developing new symptoms such as bleeding or itching.
              </li>
            </ol>
            <p className="leading-relaxed">
              If any of these features are present in a mole you have noticed, do not attempt home removal and do not delay seeking assessment. Early evaluation of a changing mole is the most important factor in outcome for any skin lesion that turns out to be malignant. Most moles assessed for ABCDE features are benign. The assessment confirms this and provides reassurance. The minority that are not benign are identified and treated at the earliest possible stage.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

const TECHNIQUE_ITEMS = [
  {
    title: "Surgical Excision",
    icon: Scissors,
    body:
      "The gold standard for any mole with concerning clinical features and for larger or deeper benign moles. The mole is removed with a margin of surrounding normal skin and the specimen is sent for histological analysis. Sutures are placed in layers to close the wound. Produces a linear scar that fades over time and is placed along natural skin lines for minimum visibility. Complete removal with confirmed clear margins is the only approach that eliminates recurrence risk and provides diagnostic certainty.",
  },
  {
    title: "Radiofrequency (RF) Shave Excision",
    icon: Zap,
    body:
      "The most commonly used technique for cosmetic removal of small to medium raised benign moles. The RF device shaves the mole level with the surrounding skin surface using controlled energy. No deep incision and no sutures are required in most cases. Produces a flat, circular healing area that typically fades to near-invisible over 3 to 6 months. Not appropriate for moles with any suspicious features as no specimen margin is taken and histological analysis is limited to the shaved material.",
  },
  {
    title: "Laser Mole Removal",
    icon: Sparkles,
    body:
      "Used for flat, pigmented moles where the pigment is targeted and destroyed by the laser energy. No physical removal of tissue occurs. The area treated forms a scab that falls off as the treated tissue heals. Appropriate for selected flat, clinically benign moles in patients where the purely cosmetic result is the priority. Not appropriate for raised moles, suspicious moles, or any lesion requiring histological analysis.",
  },
  {
    title: "Technique Selection",
    icon: UserRound,
    body:
      "The technique is determined by the mole's clinical features, size, depth, and whether it is being removed for cosmetic or medical reasons. Dr. Jain selects the technique during consultation after examining the mole. Patients should not pre-select a technique before clinical assessment as the appropriate method depends on features that can only be evaluated in person.",
  },
];

function TechniquesSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Mole Removal Techniques</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {TECHNIQUE_ITEMS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Mole Removal Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure takes 15 to 30 minutes under local anesthesia. No hospital admission is required. Dr. Jain marks the planned excision or treatment area before anesthesia is administered, ensuring the incision or treatment zone is optimally positioned for the lowest scar visibility at that specific location on the face or body.
            </p>
            <p>
              For surgical excision, an elliptical incision is made around the mole with a margin of normal surrounding skin. The mole and surrounding margin are removed as a single piece and sent for histological analysis. The wound is closed in layers with fine sutures, placing the linear scar along the natural skin tension lines of that anatomical area. For radiofrequency removal, the mole is shaved level with the skin surface using the RF device and a small dressing is applied. No sutures are required.
            </p>
            <p>
              Patients go home the same day. Written aftercare instructions are provided. Sun protection of the treated area is important during healing to prevent pigmentary changes in the healing skin.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild redness and minor swelling at the removal site. Normal and expected. A small dressing covers the area. No significant pain in most cases.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Sutures are removed for surgical excisions at this visit. A scab forms and begins to separate for RF and laser cases. The area is healing normally.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description:
        "Healing is complete for most patients. The area is flat and closed. Sun protection begins in earnest. Scar care starts for surgical excision cases.",
      icon: Clock,
    },
    {
      period: "Months 1 to 6",
      description:
        "The scar or treatment mark fades progressively. Facial mole removal sites typically show significant improvement by month 3 and continue to improve through month 6. Sun protection throughout this period significantly improves the final result.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FacialMoleSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="facial-mole-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Facial Considerations</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Facial Mole Removal: Special Considerations</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The face requires the most careful technique selection and execution in mole removal. Scars on the face are more visible than on the body, the skin is thinner and more cosmetically sensitive, and proximity to features including the eyes, lips, and nose demands precise planning. Dr. Jain places facial incisions along the natural skin tension lines, known as Langer&apos;s lines, which run in predictable directions across different areas of the face. Incisions aligned with these lines heal as narrower, less visible scars than incisions that cross them.
            </p>
            <p>
              For small raised facial moles without concerning features, radiofrequency shave excision often produces the best cosmetic outcome because it avoids the linear scar of surgical excision entirely. The circular healing area after RF removal typically fades to near-invisible on facial skin over 3 to 6 months. For moles where histological analysis is needed or where the mole is too large or deep for RF, surgical excision with meticulous closure produces a narrow scar in the most favorable orientation for that specific facial location.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldConsiderSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Who Should Consider Mole Removal?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any adult with a mole they want removed for cosmetic reasons, or a mole that has changed or shows any ABCDE features, is appropriate for consultation. There is no minimum size and no symptom requirement. A patient who dislikes the appearance of a small, clinically benign mole on their face has a valid reason for removal. A patient with a large mole that has been stable for years but causes friction under clothing has a valid reason for removal. Both are appropriate candidates.
            </p>
            <p>
              Patients with moles showing any changing features should not delay consultation waiting for the mole to grow larger or the symptoms to worsen. Early assessment of a changing mole is the most important factor in outcome if the mole turns out to require medical treatment. Patients with multiple moles they want assessed or removed can have all of them evaluated in a single consultation and suitable ones removed in the same session. Patients with a personal or family history of melanoma or multiple atypical moles benefit from regular systematic dermoscopic monitoring rather than treating individual moles in isolation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {MOLE_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`mole-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Mole Removal Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines each mole using dermoscopy to assess whether it is clinically benign or requires further evaluation, selects the appropriate removal technique for each mole&apos;s characteristics, and discusses the expected scar outcome for each location. Moles with any concerning features are treated as a medical priority. Cosmetic removals are planned with the best possible aesthetic outcome.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Sebaceous Cyst Removal",
      description: "Removal of skin lumps that are sometimes confused with raised or dome-shaped moles.",
      href: CYST_PATH,
    },
    {
      name: "Lipoma Removal",
      description: "Removal of benign fatty lumps assessed alongside skin lesion removal.",
      href: LIPOMA_PATH,
    },
    {
      name: "Scar Revision",
      description: "Improving the appearance of scars from previous mole removal attempts or trauma.",
      href: SCAR_PATH,
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function MoleRemovalPage() {
  return (
    <>
      <SEO
        title="Mole Removal | Dr. Vicky Jain"
        description="Unwanted, changing, or suspicious moles removed safely. Dr. Vicky Jain uses surgical excision, RF, and laser techniques with minimal scarring. Book today."
        canonical="/mole-removal-mumbai"
      />
      <MoleJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhenConsiderSection />
      <AbcdeSection />
      <TechniquesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <FacialMoleSection />
      <WhoShouldConsiderSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
